import { Envelope, NoteBlank } from "@phosphor-icons/react";

import RenderIf from "common/components/RenderIf";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import type { TemplateType } from "common/types";

import { useCampaignContext } from "../../../context/CampaignContext";
import { CampaignFlowProvider } from "../../../context/CampaignFlowContext";
import useFlowActions from "../../../hooks/useFlowActions";
import AutoTranslate from "./AutoTranslate";
import EndorseSkill from "./EndorseSkill";
import FlowTemplate from "./FlowTemplate";
import MessageTemplates from "./MessageTemplates";
import Placeholder from "./Placeholder";
import ReactToPost from "./ReactToPost";
import SectionHeading from "./SectionHeading";
import WithdrawConnection from "./WithdrawConnection";

import type { ReactNode } from "react";
import type { CampaignFlow as CampaignFlowType } from "../../../types";

import ViewProfile from "assets/icons/view-profile.svg?react";

type CampaignFlowProps = {
  flow: CampaignFlowType;
  isFlowError?: boolean;
  canEditFlow?: boolean;
  resetFlowError?: () => void;
};

function SectionContainer({ children }: { children: ReactNode }) {
  return (
    <div className="flex flex-col gap-y-3 border-b border-b-black-200 py-4 last:border-none last:pb-0 lg:px-1.5">
      {children}
    </div>
  );
}
export default function CampaignFlow({
  flow,
  isFlowError = false,
  canEditFlow = true,
  resetFlowError = null,
}: CampaignFlowProps) {
  const { owners } = useCampaignContext();
  const { accounts } = useSelectedWorkspaceContext();

  const workerAccounts = accounts.filter(({ id }) => owners.includes(id));

  const {
    actionErrors,
    addEmptyTemplate,
    setTranslate,
    updateFlowTemplate,
    setWithdrawDelay,
    setReactSettings,
    setEndorseSettings,
  } = useFlowActions(flow);

  const templateType: TemplateType =
    flow.type === "PRIMARY_CONNECT" ? "NOTE_TEMPLATE" : "INMAIL_TEMPLATE";
  const isInmailTemplate = templateType === "INMAIL_TEMPLATE";

  const shouldShowPremiumBanner =
    flow.type === "PRIMARY_CONNECT" &&
    !workerAccounts.every((account) => account.premium) && // All worker accounts need to have LinkedIn Premium
    templateType === "NOTE_TEMPLATE";

  return (
    <CampaignFlowProvider
      value={{ flow, canEditFlow, isFlowError, resetFlowError }}
    >
      <AutoTranslate
        translate={flow.translate}
        setTranslate={setTranslate}
        isDisabled={!canEditFlow}
      />

      <SectionContainer>
        <SectionHeading
          title="View profile"
          description="View the target’s profile immediately before connecting"
          icon={<ViewProfile />}
          shouldShowMobileTooltip
        />
      </SectionContainer>
      {/* Template Section */}
      <RenderIf
        condition={
          flow.type === "PRIMARY_CONNECT" || flow.type === "INMAIL_OPTIMIZATION"
        }
      >
        <SectionContainer>
          <SectionHeading
            title={isInmailTemplate ? "Free InMails" : "Connection Request"}
            description={
              isInmailTemplate
                ? "Send a free InMail to Premium LinkedIn users. This will never use your paid InMails"
                : "Send a connection request to your targets. This step is always active"
            }
            icon={isInmailTemplate ? <Envelope /> : <NoteBlank />}
            shouldShowMobileTooltip={false}
          />
          {flow.template ? (
            <FlowTemplate
              key={flow.template.original_id}
              flowTemplate={flow.template}
              setFlowTemplate={updateFlowTemplate}
              handleRemove={() => updateFlowTemplate(null)}
            />
          ) : (
            <Placeholder
              templateType={templateType}
              shouldShowPremiumBanner={shouldShowPremiumBanner}
              onAdd={addEmptyTemplate}
            />
          )}

          {flow.type === "INMAIL_OPTIMIZATION" && isFlowError && (
            <p className="text-xs text-red-500">
              You must select a template for InMails if InMail Optimization is
              ON
            </p>
          )}
        </SectionContainer>
      </RenderIf>

      {flow.type === "PRIMARY_CONNECT" && (
        <SectionContainer>
          <ReactToPost
            settings={{
              isEnabled: flow.like_enabled,
              reactionType: flow.like_type,
              delay: flow.like_delay,
            }}
            updateSettings={(isEnabled, reactionType, delay) =>
              setReactSettings(isEnabled, reactionType, delay)
            }
            isDisabled={!canEditFlow}
            errorMessage={actionErrors.react}
          />
        </SectionContainer>
      )}

      {flow.type !== "INMAIL_OPTIMIZATION" && (
        <SectionContainer>
          <MessageTemplates messageTemplates={flow.flow_message_templates} />
        </SectionContainer>
      )}

      {flow.type === "PRIMARY_CONNECT" && (
        <>
          <SectionContainer>
            <EndorseSkill
              settings={{
                isEnabled: flow.endorse_enabled,
                delay: flow.endorse_delay,
              }}
              updateSettings={(isEnabled, delay) =>
                setEndorseSettings(isEnabled, delay)
              }
              isDisabled={!canEditFlow}
              hasMessageSequence={!!flow.flow_message_templates.length}
            />
          </SectionContainer>
          <SectionContainer>
            <WithdrawConnection
              delay={flow.withdraw_delay}
              setDelay={setWithdrawDelay}
              isDisabled={!canEditFlow}
            />
          </SectionContainer>
        </>
      )}
    </CampaignFlowProvider>
  );
}
